import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useSelector } from 'react-redux';
import { useTodayEventCount } from '@/hooks/runner/calendar/useTodayEventCount';
import { isUnreadForUser } from '@/utils/webchat/conversationAssignment';

// Badge counts shared by the sidebar and the phone tab bar, so both always
// agree. They read the store only; NavBar loads conversations and reminders,
// and TodayEventCountLoader today's events.

interface ReminderLike {
	dateTime: string;
	isCompleted: boolean;
}

const startOfToday = () => {
	const date = new Date();
	date.setHours(0, 0, 0, 0);
	return date;
};

const endOfToday = () => {
	const date = new Date();
	date.setHours(23, 59, 59, 999);
	return date;
};

/** Open reminders that are overdue (before today) or due today. */
export function useReminderCounts() {
	const reminders = useSelector((state: any) => state.reminders?.reminders) as ReminderLike[] | undefined;
	return useMemo(() => {
		const todayStart = startOfToday();
		const todayEnd = endOfToday();
		let overdue = 0;
		let dueToday = 0;
		for (const reminder of reminders ?? []) {
			if (reminder.isCompleted) continue;
			const date = new Date(reminder.dateTime);
			if (date < todayStart) overdue++;
			else if (date <= todayEnd) dueToday++;
		}
		return { overdue, dueToday, total: overdue + dueToday };
	}, [reminders]);
}

/** Open customer conversations the user hasn't read. */
export function useInboxUnreadCount() {
	const user = useSelector((state: any) => state.auth?.user);
	const conversations = useSelector((state: any) => state.webchat?.customerConversations);
	return useMemo(
		() => (conversations ?? []).filter((c: any) => c.open !== false && isUnreadForUser(c, user)).length,
		[conversations, user],
	);
}

/** Team chats with unread messages for the user. */
export function useTeamUnreadCount() {
	const userId = useSelector((state: any) => state.auth?.user?._id);
	const teamConversations = useSelector((state: any) => state.teams?.teamConversations);
	return useMemo(
		() => (teamConversations ?? []).filter((tc: any) => tc._unreadUsers?.includes(userId)).length,
		[teamConversations, userId],
	);
}

/** Something behind the account switcher or the AI card needs attention. */
export function useDrawerAttention() {
	const auth = useSelector((state: any) => state.auth);
	const businessGroup = useSelector((state: any) => state.businessGroup);
	const pendingEscalations = useSelector((state: any) => state.aiEmployee?.pendingEscalationCount ?? 0);
	// Same rule as the account switcher's dot ("All locations" has no _id).
	const activeId = auth?.user?.isEnterpriseEnabled ? undefined : auth?._id;
	const otherLocationUnread = useMemo(
		() => (businessGroup ?? []).some((b: any) => b._id !== activeId && b.hasUnreadMessages),
		[businessGroup, activeId],
	);
	return otherLocationUnread || pendingEscalations > 0;
}

// Today's events take requests of their own (jobs, Google Calendar, Calendly),
// so one loader shares the count with every Calendar badge. It starts once a
// badge has shown (the desktop sidebar always shows one; on phones the drawer
// and Work's Today screen come and go) and then stays mounted at every width
// (DashboardFrame holds it), so rotating across md neither loads the count again
// nor blanks the badges while it does.
const todayEvents = { count: 0, wanted: false };
const todayEventListeners = new Set<() => void>();

const subscribeTodayEvents = (listener: () => void) => {
	todayEventListeners.add(listener);
	return () => {
		todayEventListeners.delete(listener);
	};
};

const updateTodayEvents = (next: Partial<typeof todayEvents>) => {
	Object.assign(todayEvents, next);
	todayEventListeners.forEach((listener) => listener());
};

/** Loads today's event count for the Calendar badges. Only DashboardFrame mounts it. */
export function TodayEventCountLoader() {
	const count = useTodayEventCount();
	useEffect(() => {
		if (count !== todayEvents.count) updateTodayEvents({ count });
	}, [count]);
	return null;
}

/** Whether a Calendar badge has been on screen, so the loader starts. */
export function useTodayEventCountWanted() {
	return useSyncExternalStore(subscribeTodayEvents, () => todayEvents.wanted);
}

/** Jobs, Google Calendar events and Calendly meetings today, for a Calendar badge. */
export function useTodayEventBadge() {
	useEffect(() => {
		if (!todayEvents.wanted) updateTodayEvents({ wanted: true });
	}, []);
	return useSyncExternalStore(subscribeTodayEvents, () => todayEvents.count);
}
